import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { LoadingState } from '@tsin-core/entities';
import { PortalDrawService } from '@tsin-core/services/http/portal-draw.service';
import { catchError, tap } from 'rxjs';
import { DeletePortalDrawById, GetActivatedPortalDraws, GetAllPortalDraws, GetSinglePortalDrawByExamId, GetSinglePortalDrawById, InitializeManualPortalDraw, InitializePortalDraw, SetSelectedDraw, UpdatePortalDraw } from './portal-draw.actions';

export class PortalDrawStateModel {
  public portalDraws: any[] = [];
  public activatedDraws: any[] = [];
  public selectedPortalDraw: any;
  public loading!: LoadingState;
  public totalApplications: number = 0;
}

const defaults = {
  portalDraws: [],
  activatedDraws: [],
  totalApplications: 0,
  selectedPortalDraw: null,
  loading: LoadingState.notLoading,
};

@State<PortalDrawStateModel>({
  name: 'portalDrawState',
  defaults,
})
@Injectable()
export class PortalDrawState {
  portalDrawService: PortalDrawService = inject(PortalDrawService);

  @Selector()
  static getPortalDraws(state: PortalDrawStateModel) {
    return state.portalDraws;
  }

  @Selector()
  static getActivatedDraws(state: PortalDrawStateModel) {
    return state.activatedDraws;
  }

  @Selector()
  static getSelectedPortalDraw(state: PortalDrawStateModel) {
    return state.selectedPortalDraw;
  }

  @Selector()
  static getTotalApplications(state: PortalDrawStateModel) {
    return state.totalApplications;
  }

  @Selector()
  static getLoading(state: PortalDrawStateModel) {
    return state.loading;
  }

  @Action(GetAllPortalDraws)
  getAllPortalDraws({ patchState }: StateContext<PortalDrawStateModel>) {
    patchState({ loading: LoadingState.loadingList });
    return this.portalDrawService.getAllDraws().pipe(
      tap((portalDraws: any) => {
        patchState({ portalDraws, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(GetActivatedPortalDraws)
  getActivatedPortalDraws({ patchState }: StateContext<PortalDrawStateModel>) {
    patchState({ loading: LoadingState.loadingList });
    return this.portalDrawService.getActivatedDraws().pipe(
      tap((activatedDraws: any) => {
        patchState({ activatedDraws, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(InitializePortalDraw)
  initializePortalDraw({ getState, patchState }: StateContext<PortalDrawStateModel>, action: InitializePortalDraw) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.portalDrawService.initializeDraw(action.payload).pipe(
      tap((portalDraw: any) => {
        const portalDraws = getState().portalDraws;
        patchState({ portalDraws: [...portalDraws, portalDraw], loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(InitializeManualPortalDraw)
  initializeManualPortalDraw({ getState, patchState }: StateContext<PortalDrawStateModel>, action: InitializeManualPortalDraw) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.portalDrawService.initializeManualDraw(action.payload).pipe(
      tap((portalDraw: any) => {
        const portalDraws = getState().portalDraws;
        patchState({ portalDraws: [...portalDraws, portalDraw], loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(UpdatePortalDraw)
  updatePortalDraw({ patchState }: StateContext<PortalDrawStateModel>, action: UpdatePortalDraw) {
    patchState({ loading: LoadingState.loadingList });
    return this.portalDrawService.updateDraw(action.payload).pipe(
      tap((portalDraw: any) => {
        patchState({ selectedPortalDraw: portalDraw, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(GetSinglePortalDrawByExamId)
  getSinglePortalDrawByExamId({ patchState }: StateContext<PortalDrawStateModel>, action: GetSinglePortalDrawByExamId) {
    patchState({ loading: LoadingState.loadingList });
    return this.portalDrawService.getSingleDrawByExamId(action.payload).pipe(
      tap((portalDraw: any) => {
        patchState({ portalDraws: portalDraw.draws, totalApplications: portalDraw.totalApplications, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(GetSinglePortalDrawById)
  getSinglePortalDrawById({ patchState }: StateContext<PortalDrawStateModel>, action: GetSinglePortalDrawById) {
    patchState({ loading: LoadingState.loadingList });
    return this.portalDrawService.getSingleDrawById(action.payload).pipe(
      tap((portalDraw: any) => {
        patchState({ selectedPortalDraw: portalDraw, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(DeletePortalDrawById)
  deletePortalDrawById({ patchState }: StateContext<PortalDrawStateModel>, action: DeletePortalDrawById) {
    patchState({ loading: LoadingState.loadingList });
    return this.portalDrawService.deleteDrawById(action.payload).pipe(
      tap(() => {
        patchState({ selectedPortalDraw: null, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(SetSelectedDraw)
  setSelectedDraw({ patchState }: StateContext<PortalDrawStateModel>, action: SetSelectedDraw) {
    patchState({ selectedPortalDraw: action.payload });
  }
}
